/**
 * SaaS Market Map 2026 — real data extracted from SaaS_Market_Map_2026.xlsx.
 * 316 tools across 41 categories (NZ / AU / SG / USA landscape, June 2026).
 *
 * `lens` comes from the map's "Can RCRT replace" column:
 *   'replace'   — explicitly marked Replace (the category's job becomes an
 *                 agent + breadcrumbs + a connector on the rcrt runtime)
 *   'integrate' — explicitly marked Integrate (system of record rcrt
 *                 orchestrates on top of; its spend is retained)
 *   undefined   — unmarked in the map; confirmed in a discovery session
 */

export type Lens = 'replace' | 'integrate';

export interface MapTool {
  name: string;
  sub: string;
  what: string;
  lens?: Lens;
}

export interface MapCategory {
  name: string;
  tools: MapTool[];
}

export const MARKET_MAP: MapCategory[] = [
  {
    name: "Foundation Models & AI Assistants",
    tools: [
      { name: "ChatGPT (OpenAI)", sub: "General assistant", what: "General-purpose AI assistant; default consumer + prosumer AI" },
      { name: "Claude (Anthropic)", sub: "General assistant", what: "Assistant strong on reasoning, coding, long-context, agents" },
      { name: "Gemini (Google)", sub: "General assistant", what: "Assistant integrated with Workspace and Android" },
      { name: "Microsoft Copilot", sub: "Embedded copilot", what: "AI layer across Microsoft 365 and Windows" },
      { name: "Perplexity", sub: "Answer engine", what: "AI answer engine with cited web research" },
      { name: "Mistral (Le Chat)", sub: "Open-weight", what: "Open-weight assistant; sovereign / EU option" },
      { name: "Meta Llama", sub: "Open model", what: "Open-weight model family for self-hosting" },
      { name: "Amazon Bedrock", sub: "Model API", what: "Managed multi-model API on AWS" },
      { name: "Azure OpenAI Service", sub: "Model API", what: "Enterprise-governed OpenAI models on Azure" },
    ],
  },
  {
    name: "AI Coding & Developer Tools",
    tools: [
      { name: "Cursor (Anysphere)", sub: "AI IDE", what: "AI-native IDE with parallel coding agents" },
      { name: "Claude Code (Anthropic)", sub: "CLI agent", what: "Terminal agent for large-codebase, hard problems" },
      { name: "GitHub Copilot", sub: "In-editor copilot", what: "In-editor AI completion plus agents" },
      { name: "OpenAI Codex", sub: "Coding agent", what: "Cloud + IDE coding agent" },
      { name: "Devin (Cognition)", sub: "Autonomous SWE", what: "Autonomous software-engineer agent" },
      { name: "Replit", sub: "Build/deploy", what: "Browser build/deploy with AI agent", lens: 'replace' },
      { name: "Lovable", sub: "App generation", what: "Full-stack apps from prompts (vibe coding)", lens: 'replace' },
      { name: "v0 (Vercel)", sub: "UI generation", what: "UI/app generation from prompts", lens: 'replace' },
      { name: "GitHub", sub: "Source hosting", what: "Git repos, CI/CD, code collaboration", lens: 'integrate' },
      { name: "GitLab", sub: "Source hosting", what: "DevSecOps platform with built-in CI/CD", lens: 'integrate' },
      { name: "Atlassian Bitbucket", sub: "Source hosting", what: "Git hosting tied to Jira ecosystem", lens: 'integrate' },
    ],
  },
  {
    name: "AI Agents & Automation",
    tools: [
      { name: "Zapier", sub: "Workflow automation", what: "No-code app-to-app automation + AI agents", lens: 'replace' },
      { name: "Make (Integromat)", sub: "Workflow automation", what: "Visual multi-step automation platform", lens: 'replace' },
      { name: "n8n", sub: "Workflow + agents", what: "Source-available automation with native AI agents", lens: 'replace' },
      { name: "Relevance AI", sub: "Agent builder", what: "No-code AI agent/agent-team builder", lens: 'replace' },
      { name: "Lindy", sub: "Personal agents", what: "AI agents for email, calendar, ops", lens: 'replace' },
      { name: "MuleSoft (Salesforce)", sub: "iPaaS", what: "Enterprise API integration platform", lens: 'replace' },
      { name: "Workato", sub: "iPaaS", what: "Enterprise automation + integration (recipes)", lens: 'replace' },
      { name: "Boomi", sub: "iPaaS", what: "Cloud integration and data management", lens: 'replace' },
      { name: "UiPath", sub: "RPA", what: "Robotic process automation + agentic AI", lens: 'replace' },
      { name: "Automation Anywhere", sub: "RPA", what: "Cloud RPA with AI agents", lens: 'replace' },
    ],
  },
  {
    name: "CRM & Sales",
    tools: [
      { name: "Salesforce", sub: "Enterprise CRM", what: "Cloud CRM + apps platform (Agentforce AI)", lens: 'replace' },
      { name: "HubSpot", sub: "SMB CRM", what: "All-in-one CRM, marketing, sales, service", lens: 'replace' },
      { name: "Pipedrive", sub: "Sales-led CRM", what: "Pipeline-first sales CRM", lens: 'replace' },
      { name: "Attio", sub: "Modern CRM", what: "AI-native, data-model-flexible CRM", lens: 'replace' },
      { name: "Close", sub: "Inbox CRM", what: "CRM with built-in calling + email for SMB", lens: 'replace' },
      { name: "Microsoft Dynamics 365", sub: "CRM (MS)", what: "CRM + ERP suite on Microsoft stack", lens: 'replace' },
      { name: "Zoho CRM", sub: "CRM (open)", what: "Affordable CRM within broad Zoho suite", lens: 'replace' },
      { name: "DealHub / Salesforce CPQ", sub: "CPQ/Revenue", what: "Quote-to-cash and CPQ tooling", lens: 'replace' },
    ],
  },
  {
    name: "Sales Intelligence & Engagement",
    tools: [
      { name: "Clay", sub: "GTM data", what: "AI GTM data enrichment + prospecting automation", lens: 'integrate' },
      { name: "Apollo.io", sub: "Sales intelligence", what: "Contact database + engagement + AI assistant", lens: 'integrate' },
      { name: "ZoomInfo", sub: "B2B data", what: "B2B contact and company intelligence", lens: 'integrate' },
      { name: "Gong", sub: "Revenue intelligence", what: "Call recording + conversation/revenue analytics", lens: 'integrate' },
      { name: "Outreach", sub: "Sales engagement", what: "Sequenced outbound engagement platform", lens: 'integrate' },
      { name: "Salesloft", sub: "Sales engagement", what: "Revenue workflow + engagement platform", lens: 'integrate' },
      { name: "11x", sub: "AI SDR", what: "Autonomous AI sales reps (digital workers)", lens: 'replace' },
      { name: "Lusha", sub: "Prospecting", what: "B2B contact data + prospecting", lens: 'replace' },
    ],
  },
  {
    name: "Marketing & Email",
    tools: [
      { name: "Marketo (Adobe)", sub: "Marketing automation", what: "Enterprise B2B marketing automation", lens: 'replace' },
      { name: "Mailchimp (Intuit)", sub: "Email/ESP", what: "SMB email marketing + automation", lens: 'replace' },
      { name: "Klaviyo", sub: "Ecommerce CRM", what: "Email/SMS marketing for ecommerce", lens: 'replace' },
      { name: "ActiveCampaign", sub: "Email/CRM", what: "Email + CRM + automation for SMB", lens: 'replace' },
      { name: "Braze", sub: "Transactional/marketing", what: "Cross-channel customer engagement", lens: 'replace' },
      { name: "Customer.io", sub: "Customer messaging", what: "Behavioural messaging automation", lens: 'replace' },
      { name: "Brevo (Sendinblue)", sub: "Email/SMS", what: "Affordable email/SMS/marketing suite", lens: 'integrate' },
      { name: "Twilio SendGrid", sub: "Email infrastructure", what: "Transactional + marketing email API", lens: 'integrate' },
    ],
  },
  {
    name: "SEO, Content & Social",
    tools: [
      { name: "Semrush", sub: "SEO suite", what: "SEO, content and competitive marketing toolkit" },
      { name: "Ahrefs", sub: "SEO suite", what: "Backlink, keyword and SEO analysis" },
      { name: "Hootsuite", sub: "Social management", what: "Social scheduling and management" },
      { name: "Buffer", sub: "Social management", what: "Simple social scheduling" },
      { name: "Sprout Social", sub: "Social/marketing", what: "Social management + analytics + care" },
      { name: "Jasper", sub: "AI writing", what: "Enterprise AI content + marketing workflows" },
      { name: "Copy.ai", sub: "AI GTM content", what: "AI GTM platform (content + outreach)" },
      { name: "Writer", sub: "Enterprise AI writing", what: "Governed enterprise generative AI platform" },
      { name: "Linktree", sub: "Link-in-bio", what: "Link-in-bio hub for creators" },
      { name: "Tracksuit", sub: "Brand tracking", what: "Always-on brand awareness tracking" },
    ],
  },
  {
    name: "Analytics & Experimentation",
    tools: [
      { name: "Google Analytics 4", sub: "Web analytics", what: "Web/app traffic and conversion analytics" },
      { name: "Amplitude", sub: "Product analytics", what: "Product usage and behavioural analytics" },
      { name: "Mixpanel", sub: "Product analytics", what: "Event-based product analytics" },
      { name: "PostHog", sub: "Product + replay", what: "Open-source product analytics + session replay" },
      { name: "Segment (Twilio)", sub: "Tag/data", what: "Customer data pipeline / CDP" },
      { name: "Optimizely", sub: "Experimentation", what: "Experimentation + digital experience" },
      { name: "Hotjar", sub: "Session replay", what: "Heatmaps and session recordings" },
    ],
  },
  {
    name: "Customer Support & Service",
    tools: [
      { name: "Zendesk", sub: "Help desk", what: "Omnichannel support ticketing + AI agents" },
      { name: "Intercom (Fin)", sub: "Messaging support", what: "In-product messaging + AI resolution agent" },
      { name: "Freshdesk (Freshworks)", sub: "Help desk", what: "Support ticketing within Freshworks suite", lens: 'replace' },
      { name: "Salesforce Service Cloud", sub: "Service (CRM)", what: "Enterprise service + Agentforce" },
      { name: "Sierra", sub: "CX AI agent", what: "AI customer-experience agents" },
      { name: "Decagon", sub: "CX AI agent", what: "AI support agents for tech firms" },
      { name: "Ada", sub: "CX AI agent", what: "Automated customer-service agents" },
      { name: "Cresta", sub: "Contact centre AI", what: "AI for contact centres (live + coaching)" },
      { name: "Genesys Cloud", sub: "CCaaS", what: "Cloud contact-centre platform" },
      { name: "Guru", sub: "Knowledge", what: "AI enterprise knowledge / wiki for teams" },
    ],
  },
  {
    name: "Accounting & Finance (SMB)",
    tools: [
      { name: "Xero", sub: "Cloud accounting", what: "Cloud accounting for SMBs and advisors" },
      { name: "MYOB", sub: "Accounting/ERP", what: "Accounting + business management, moving to ERP" },
      { name: "QuickBooks (Intuit)", sub: "Accounting", what: "SMB accounting + payroll" },
      { name: "Reckon", sub: "Accounting", what: "Accounting + practice software" },
      { name: "Hnry", sub: "Sole-trader tax", what: "Automated accounting + tax for sole traders" },
      { name: "Financio / ABSS", sub: "Accounting (SG)", what: "SME accounting for SEA" },
      { name: "FreshBooks", sub: "Invoicing", what: "Invoicing + accounting for freelancers" },
      { name: "Unleashed / Cin7", sub: "Inventory accounting", what: "Inventory + order management for SMB" },
    ],
  },
  {
    name: "ERP & Enterprise Finance",
    tools: [
      { name: "NetSuite (Oracle)", sub: "Cloud ERP", what: "Cloud ERP for mid-market" },
      { name: "SAP S/4HANA", sub: "ERP", what: "Enterprise resource planning suite" },
      { name: "Oracle Fusion", sub: "ERP/finance", what: "Enterprise ERP, HCM, finance cloud" },
      { name: "Workday", sub: "Finance + HR", what: "Cloud finance + HCM for enterprise" },
      { name: "TechnologyOne", sub: "Public-sector ERP", what: "SaaS ERP for govt, education, health" },
      { name: "Microsoft Dynamics 365 BC", sub: "Mid ERP", what: "ERP for SMB/mid-market on MS stack" },
      { name: "Anaplan", sub: "FP&A", what: "Connected planning and modelling" },
      { name: "Cube / Planful", sub: "FP&A (SMB)", what: "Mid-market FP&A and planning" },
    ],
  },
  {
    name: "Spend, Expense & Procurement",
    tools: [
      { name: "Ramp", sub: "Spend management", what: "Corporate cards + AI spend/finance automation" },
      { name: "Brex", sub: "Spend management", what: "Corporate cards + spend for startups" },
      { name: "Expensify", sub: "Expense", what: "Receipt + expense automation" },
      { name: "SAP Concur", sub: "Travel + expense", what: "Enterprise travel and expense" },
      { name: "Bill.com", sub: "AP automation", what: "Accounts payable / receivable automation" },
      { name: "Coupa", sub: "Procurement", what: "Business spend / procurement suite" },
      { name: "Weel (ex-DiviPay)", sub: "Expense (ANZ)", what: "Corporate cards + spend for ANZ SMBs" },
      { name: "Vertice / Spendflo", sub: "Subscription mgmt", what: "SaaS spend + subscription optimisation" },
    ],
  },
  {
    name: "Payments & Billing",
    tools: [
      { name: "Stripe", sub: "Payments API", what: "Online payments + financial infrastructure" },
      { name: "Adyen", sub: "Payments", what: "Unified global payments platform" },
      { name: "PayPal / Braintree", sub: "Payments/checkout", what: "Online payments and checkout" },
      { name: "Square (Block)", sub: "Commerce + POS", what: "Payments + POS + business tools" },
      { name: "Chargebee", sub: "Subscription billing", what: "Subscription management and billing" },
      { name: "Stripe Billing / Metronome", sub: "Usage billing", what: "Usage-based and recurring billing" },
      { name: "Tyro", sub: "Merchant (ANZ)", what: "EFTPOS + merchant acquiring + banking" },
      { name: "Airwallex", sub: "Cross-border", what: "Cross-border payments + multi-currency accounts" },
    ],
  },
  {
    name: "Fintech, Banking & Lending",
    tools: [
      { name: "Afterpay (Block)", sub: "BNPL", what: "Buy-now-pay-later at checkout" },
      { name: "Zip", sub: "BNPL", what: "Buy-now-pay-later platform" },
      { name: "Prospa", sub: "SME lending", what: "SME lending fed by accounting data" },
      { name: "Nium", sub: "Cross-border infra", what: "Cross-border payments infrastructure" },
      { name: "Aspire", sub: "SME finance", what: "All-in-one SME finance (accounts, cards, spend)" },
      { name: "Funding Societies", sub: "SME lending", what: "SME P2P lending marketplace" },
      { name: "StashAway / Endowus", sub: "Digital wealth", what: "Digital wealth and investing" },
      { name: "Sharesies", sub: "Retail investing", what: "Low-cost retail investment platform" },
    ],
  },
  {
    name: "HR, HRIS & People",
    tools: [
      { name: "Employment Hero", sub: "Employment OS (ANZ)", what: "All-in-one HR + payroll employment platform" },
      { name: "BambooHR", sub: "HRIS (SMB)", what: "HRIS for small and mid businesses" },
      { name: "HiBob", sub: "Global HRIS", what: "Modern HRIS for mid-market" },
      { name: "ELMO", sub: "HR + L&D (ANZ)", what: "HR, payroll and L&D suite" },
      { name: "Culture Amp", sub: "Engagement", what: "Employee engagement + people analytics" },
      { name: "Deel", sub: "EOR / global", what: "Global payroll, EOR and contractor management" },
      { name: "Remote.com", sub: "EOR / global", what: "Employer-of-record and global payroll" },
      { name: "Talenox", sub: "HR (SG)", what: "Payroll + HR with CPF/IR8A automation" },
      { name: "Swingvy / JustLogin", sub: "HR (SG)", what: "Cloud HR for SEA SMEs" },
      { name: "Lattice", sub: "Performance", what: "Performance, goals and engagement" },
    ],
  },
  {
    name: "Payroll & Workforce",
    tools: [
      { name: "Deputy", sub: "Workforce mgmt", what: "Rostering, time and attendance" },
      { name: "Tanda", sub: "Workforce mgmt", what: "Rostering, time + wage compliance" },
      { name: "PayHero", sub: "Payroll (NZ)", what: "Payroll for variable-hour staff" },
      { name: "Smartly", sub: "Payroll (NZ)", what: "Payroll + people management" },
      { name: "iPayroll", sub: "Payroll (NZ)", what: "Cloud payroll for NZ" },
      { name: "Dayforce (ex-Ceridian)", sub: "Enterprise HCM", what: "Enterprise HCM + payroll (incl. Ascender)" },
      { name: "Gusto", sub: "Payroll (US)", what: "SMB payroll, benefits and HR" },
      { name: "UKG", sub: "Workforce (enterprise)", what: "Enterprise workforce + HCM" },
    ],
  },
  {
    name: "Recruiting & Talent",
    tools: [
      { name: "Greenhouse", sub: "ATS (enterprise)", what: "Structured hiring + ATS" },
      { name: "Workday Recruiting", sub: "ATS (enterprise)", what: "Recruiting within Workday HCM" },
      { name: "JobAdder", sub: "ATS (SMB)", what: "Recruitment + ATS for agencies/SMB" },
      { name: "Lever / Ashby", sub: "ATS", what: "Modern ATS + recruiting analytics" },
      { name: "Mercor", sub: "AI recruiting", what: "AI hiring/vetting marketplace" },
      { name: "SEEK", sub: "Job marketplace", what: "Dominant ANZ/SEA job marketplace" },
      { name: "Checkr", sub: "Background checks", what: "Automated background screening" },
      { name: "HackerRank / Criteria", sub: "Assessments", what: "Skills and coding assessments" },
    ],
  },
  {
    name: "Learning & Development",
    tools: [
      { name: "Go1", sub: "Content marketplace", what: "Corporate learning content aggregator" },
      { name: "Docebo", sub: "LMS", what: "AI-powered enterprise LMS" },
      { name: "Moodle", sub: "LMS (open)", what: "Open-source LMS" },
      { name: "Udemy Business / Coursera", sub: "Skills/courses", what: "On-demand professional course libraries" },
      { name: "Lessonly / WorkRamp", sub: "Sales/onboarding", what: "Training and enablement" },
      { name: "Litmos", sub: "Compliance LMS", what: "Compliance and corporate training" },
    ],
  },
  {
    name: "Productivity & Office Suites",
    tools: [
      { name: "Microsoft 365", sub: "Office suite", what: "Office apps, email, Teams, storage" },
      { name: "Google Workspace", sub: "Office suite", what: "Email, docs, drive, meet" },
      { name: "Notion", sub: "Notes/AI", what: "Docs, wiki, projects + AI" },
      { name: "Coda", sub: "Docs/wiki", what: "Docs that act like apps" },
      { name: "Evernote / OneNote", sub: "Note-taking", what: "Personal and team note capture" },
      { name: "Miro", sub: "Whiteboard", what: "Collaborative online whiteboard" },
      { name: "FigJam (Figma)", sub: "Whiteboard", what: "Collaborative whiteboard for design teams" },
    ],
  },
  {
    name: "Collaboration & Communication",
    tools: [
      { name: "Slack (Salesforce)", sub: "Team chat", what: "Team messaging + workflow + AI" },
      { name: "Microsoft Teams", sub: "Chat + meetings", what: "Chat, meetings, calling in M365" },
      { name: "Zoom", sub: "Video", what: "Video meetings + AI companion" },
      { name: "Google Meet", sub: "Video (Google)", what: "Video meetings in Workspace" },
      { name: "Twilio", sub: "Comms API", what: "Programmable SMS, voice, messaging" },
      { name: "RingCentral / Aircall", sub: "Business phone", what: "Cloud phone and contact tools" },
      { name: "Discord / Circle", sub: "Community", what: "Community + creator messaging" },
    ],
  },
  {
    name: "Project & Work Management",
    tools: [
      { name: "Jira (Atlassian)", sub: "Dev project tracking", what: "Agile project + issue tracking" },
      { name: "Asana", sub: "Work management", what: "Tasks, projects, workflows" },
      { name: "Monday.com", sub: "Work OS", what: "Visual work operating system" },
      { name: "ClickUp", sub: "All-in-one", what: "Tasks, docs, goals in one app" },
      { name: "Linear", sub: "Dev planning", what: "Fast issue tracking for software teams" },
      { name: "Trello (Atlassian)", sub: "Lists/tasks", what: "Kanban boards for light projects" },
      { name: "Airtable", sub: "Spreadsheet-DB", what: "Database-spreadsheet hybrid + AI" },
      { name: "Smartsheet", sub: "PPM", what: "Work + portfolio management at scale" },
      { name: "Karbon", sub: "PSA", what: "Practice/workflow management for firms" },
    ],
  },
  {
    name: "Knowledge & Enterprise Search",
    tools: [
      { name: "Confluence (Atlassian)", sub: "Wiki", what: "Team wiki and documentation" },
      { name: "Glean", sub: "Enterprise search", what: "Unified enterprise search + work AI assistant" },
      { name: "Perplexity Enterprise", sub: "Enterprise search", what: "AI research over internal docs + web" },
      { name: "Hebbia", sub: "Domain search", what: "AI search/analysis over proprietary docs" },
      { name: "Document360 / Slab", sub: "Docs platform", what: "Knowledge base and documentation" },
    ],
  },
  {
    name: "Design & Creative",
    tools: [
      { name: "Canva", sub: "Design platform", what: "Drag-and-drop design + AI for everyone" },
      { name: "Figma", sub: "Product design", what: "Collaborative interface design + AI" },
      { name: "Adobe Creative Cloud (Firefly)", sub: "Creative suite", what: "Pro creative apps + generative AI" },
      { name: "Midjourney", sub: "AI image", what: "AI image (and emerging video) generation" },
      { name: "Ideogram", sub: "AI image", what: "AI image gen strong on text rendering" },
      { name: "Runway", sub: "AI video", what: "AI video generation (leading quality)" },
      { name: "Synthesia", sub: "AI avatar video", what: "AI avatar video for corporate content" },
      { name: "HeyGen", sub: "AI video hub", what: "AI avatars + multi-model video hub" },
      { name: "ElevenLabs", sub: "AI voice", what: "AI voice and audio generation" },
      { name: "Descript", sub: "AI media editing", what: "AI-native audio/video editing" },
    ],
  },
  {
    name: "Cloud Infrastructure & DevOps",
    tools: [
      { name: "Amazon Web Services", sub: "Cloud platform", what: "Cloud compute, storage, AI services" },
      { name: "Microsoft Azure", sub: "Cloud platform", what: "Enterprise cloud + AI services" },
      { name: "Google Cloud", sub: "Cloud platform", what: "Cloud + data/AI (Vertex AI)" },
      { name: "Vercel / Netlify", sub: "PaaS", what: "Front-end hosting and deployment" },
      { name: "Docker / Kubernetes", sub: "Containers", what: "Containerisation + orchestration" },
      { name: "Datadog", sub: "Observability", what: "Monitoring + observability + security" },
      { name: "PagerDuty / Opsgenie", sub: "Incident", what: "On-call and incident response" },
      { name: "Sentry", sub: "Errors", what: "Application error and performance monitoring" },
      { name: "Cloudflare", sub: "CDN/security", what: "CDN, security and edge platform" },
    ],
  },
  {
    name: "Data, Analytics & BI",
    tools: [
      { name: "Snowflake", sub: "Data warehouse", what: "Cloud data warehouse / data cloud" },
      { name: "Databricks", sub: "Lakehouse", what: "Data + AI lakehouse platform" },
      { name: "Google BigQuery", sub: "Warehouse", what: "Serverless cloud data warehouse" },
      { name: "Power BI (Microsoft)", sub: "BI", what: "Business intelligence dashboards" },
      { name: "Tableau (Salesforce)", sub: "BI", what: "Visual analytics and dashboards" },
      { name: "Looker (Google)", sub: "BI", what: "Modelled BI and embedded analytics" },
      { name: "ThoughtSpot", sub: "NL analytics", what: "Search-first / natural-language BI" },
      { name: "Hex", sub: "AI notebooks", what: "AI-assisted SQL/Python notebooks" },
      { name: "Julius", sub: "NL data", what: "No-code natural-language data analysis" },
      { name: "Fivetran / dbt", sub: "ETL/ELT", what: "Data ingestion + transformation" },
    ],
  },
  {
    name: "Security & Identity",
    tools: [
      { name: "Okta", sub: "Identity", what: "Identity + access management (SSO)" },
      { name: "Microsoft Entra ID", sub: "Identity (MS)", what: "Enterprise identity within Microsoft" },
      { name: "1Password", sub: "Password mgmt", what: "Password and secrets management" },
      { name: "CrowdStrike", sub: "Endpoint", what: "Endpoint detection and response" },
      { name: "Microsoft Defender", sub: "Endpoint (MS)", what: "Endpoint + cloud security suite" },
      { name: "Splunk (Cisco)", sub: "SIEM", what: "Security/log analytics and SIEM" },
      { name: "Vanta / Drata", sub: "Compliance", what: "Automated security compliance (SOC 2, ISO)" },
      { name: "Abnormal / Mimecast", sub: "Email security", what: "AI email threat protection" },
      { name: "Zscaler / Palo Alto", sub: "Network", what: "Cloud network security (SASE/ZTNA)" },
    ],
  },
  {
    name: "IT Service Mgmt & Endpoint",
    tools: [
      { name: "ServiceNow", sub: "ITSM", what: "Enterprise IT service + workflow platform" },
      { name: "Freshservice / Jira SM", sub: "ITSM (SMB)", what: "IT service desk for mid-market" },
      { name: "Jamf", sub: "Device mgmt", what: "Apple device management (MDM)" },
      { name: "Microsoft Intune", sub: "Device mgmt", what: "Unified endpoint management" },
      { name: "Atera / NinjaOne", sub: "RMM/IT", what: "Remote monitoring + IT management" },
      { name: "Zluri / Torii", sub: "Asset/SaaS mgmt", what: "SaaS management and license optimisation" },
    ],
  },
  {
    name: "E-commerce, Retail & POS",
    tools: [
      { name: "Shopify", sub: "Ecommerce platform", what: "Online store + commerce platform" },
      { name: "BigCommerce / Adobe Commerce", sub: "Ecommerce (enterprise)", what: "Enterprise/mid-market ecommerce" },
      { name: "commercetools", sub: "Headless commerce", what: "API-first headless commerce" },
      { name: "Lightspeed", sub: "Retail/hospo POS", what: "Retail + hospitality POS (ex-Vend/Kounta)" },
      { name: "Marketplacer", sub: "Marketplace tools", what: "Marketplace platform for retailers" },
      { name: "me&u / Mr Yum", sub: "Hospitality order", what: "At-table QR order-and-pay" },
      { name: "Yotpo / Okendo", sub: "Reviews/UGC", what: "Reviews, loyalty and UGC for ecommerce" },
      { name: "Recharge / Ordergroove", sub: "Subscriptions", what: "Ecommerce subscription management" },
    ],
  },
  {
    name: "Field Service & Trades",
    tools: [
      { name: "ServiceM8", sub: "Job mgmt (micro)", what: "Job management for small trades" },
      { name: "Tradify", sub: "Job mgmt", what: "Job management for tradies" },
      { name: "Fergus", sub: "Job mgmt", what: "Job mgmt with job costing/margin" },
      { name: "simPRO", sub: "Field service ERP", what: "Enterprise field-service/trade ERP" },
      { name: "AroFlo", sub: "Field service", what: "Field service with inventory/materials" },
      { name: "NextMinute / Ascora", sub: "Job mgmt", what: "Job + time management for trades" },
      { name: "ServiceTitan", sub: "Field service (global)", what: "Field service management platform" },
      { name: "Salesforce Field Service", sub: "FSM (enterprise)", what: "Enterprise field service on Salesforce" },
    ],
  },
  {
    name: "Property & Real Estate",
    tools: [
      { name: "PropertyMe", sub: "Property mgmt", what: "Residential property mgmt + trust accounting" },
      { name: "Console Cloud", sub: "Property mgmt", what: "Property mgmt + trust accounting" },
      { name: "Palace", sub: "Property mgmt", what: "Property management + trust accounting" },
      { name: "Tapi", sub: "Maintenance layer", what: "Maintenance workflow over PM systems" },
      { name: "Re-Leased", sub: "Commercial PM", what: "Commercial property management" },
      { name: "MRI Software / Property Tree", sub: "Property (enterprise)", what: "Property mgmt (incl. ex-Rockend)" },
      { name: "Yardi / VTS", sub: "CRE platform", what: "Commercial real estate + asset mgmt" },
      { name: "Rex / AgentBox", sub: "Agent CRM", what: "Real estate agency CRM" },
    ],
  },
  {
    name: "Construction",
    tools: [
      { name: "Procore", sub: "Construction mgmt", what: "Enterprise construction management" },
      { name: "Buildxact", sub: "Estimating", what: "Estimating + job costing" },
      { name: "Buildertrend", sub: "Builder PM", what: "Residential construction project mgmt" },
      { name: "Databuild / Built", sub: "Estimating (ANZ)", what: "Estimating and builder management" },
      { name: "Autodesk Construction Cloud", sub: "BIM/design", what: "BIM + construction collaboration" },
      { name: "SafetyCulture", sub: "Safety/compliance", what: "Inspections, safety, frontline operations" },
    ],
  },
  {
    name: "Legal & Contract",
    tools: [
      { name: "Harvey", sub: "AI legal", what: "AI agents for legal work" },
      { name: "Legora", sub: "AI legal", what: "AI legal platform (Harvey rival)" },
      { name: "Ironclad / DocuSign CLM", sub: "CLM", what: "Contract lifecycle management" },
      { name: "DocuSign", sub: "eSignature", what: "Electronic signature + agreements", lens: 'replace' },
      { name: "Annature / FuseSign", sub: "eSignature (ANZ)", what: "eSignature + ID verification", lens: 'replace' },
      { name: "Clio / Smokeball", sub: "Practice mgmt", what: "Legal practice management", lens: 'replace' },
      { name: "Luminance / Spellbook", sub: "Contract analysis", what: "AI contract review + drafting", lens: 'replace' },
    ],
  },
  {
    name: "Practice Mgmt & Pro Services",
    tools: [
      { name: "FYI", sub: "Accounting workflow", what: "Document mgmt + automation for firms" },
      { name: "Ignition", sub: "Proposals/billing", what: "Proposals, engagement, billing + payments", lens: 'replace' },
      { name: "Xero Practice Manager", sub: "Practice mgmt", what: "Practice management for firms" },
      { name: "Accelo", sub: "PSA", what: "Service ops / PSA for agencies", lens: 'replace' },
      { name: "Proposify / PandaDoc", sub: "Proposals", what: "Proposal + document automation", lens: 'replace' },
      { name: "Harvest / WorkflowMax", sub: "Time/billing", what: "Time tracking and billing" },
    ],
  },
  {
    name: "Healthcare & Clinical",
    tools: [
      { name: "Abridge", sub: "Ambient AI scribe", what: "Ambient AI clinical documentation" },
      { name: "Best Practice / MedicalDirector", sub: "GP software (AU)", what: "GP clinical + practice management" },
      { name: "Indici / Medtech", sub: "Patient mgmt (NZ)", what: "Primary-care patient management" },
      { name: "Genie / MediRecords", sub: "Specialist PM", what: "Specialist + clinical practice mgmt" },
      { name: "Epic / Oracle Health", sub: "EHR (enterprise)", what: "Enterprise electronic health records" },
      { name: "HotDoc / HealthEngine", sub: "Allied/booking", what: "Patient booking and engagement" },
      { name: "Teladoc / Coviu", sub: "Telehealth", what: "Telehealth consultation platforms" },
    ],
  },
  {
    name: "Education & EdTech",
    tools: [
      { name: "Education Perfect", sub: "Learning platform", what: "Online teaching and learning" },
      { name: "Compass / Sentral", sub: "School mgmt", what: "School management + communication" },
      { name: "Edval", sub: "Timetabling", what: "School timetabling and scheduling" },
      { name: "Canvas (Instructure)", sub: "LMS (edu)", what: "Higher-ed and K-12 LMS" },
      { name: "Mathletics / Education Perfect", sub: "Maths/learning", what: "Curriculum learning content" },
      { name: "TechnologyOne Student Mgmt", sub: "Student admin", what: "Student management for tertiary" },
    ],
  },
  {
    name: "Meetings, Transcription & Voice",
    tools: [
      { name: "Fathom", sub: "AI notetaker", what: "Frictionless recording, transcripts, summaries" },
      { name: "Granola", sub: "AI notetaker", what: "Bot-free Mac-native AI notes" },
      { name: "Otter.ai", sub: "AI notetaker", what: "Transcription + meeting assistant" },
      { name: "Fireflies.ai", sub: "AI notetaker", what: "Notetaker with deep CRM coverage" },
      { name: "Read.ai", sub: "Meeting analytics", what: "Meeting analytics + assistant" },
      { name: "Vapi / Retell", sub: "Voice agents", what: "Build AI voice phone agents" },
    ],
  },
  {
    name: "Forms & Document Automation",
    tools: [
      { name: "Typeform", sub: "Forms", what: "Conversational forms and surveys" },
      { name: "Google Forms / Jotform", sub: "Forms (Google)", what: "Simple forms and data collection" },
      { name: "Documint / Formstack", sub: "Doc generation", what: "Automated document generation" },
      { name: "Adobe Acrobat / PDF tools", sub: "PDF tools", what: "PDF creation, editing, signing" },
      { name: "Calendly", sub: "Scheduling", what: "Meeting scheduling automation" },
    ],
  },
  {
    name: "No-code, App & Web Builders",
    tools: [
      { name: "Bubble", sub: "App builder", what: "No-code web app builder" },
      { name: "Retool", sub: "Internal tools", what: "Build internal tools fast + AI" },
      { name: "Webflow", sub: "Website", what: "Visual website design + CMS" },
      { name: "Wix / Squarespace", sub: "Website (SMB)", what: "DIY websites for small business" },
      { name: "WordPress / Contentful", sub: "CMS", what: "Content management / headless CMS" },
      { name: "Airtable / Glide", sub: "Database", what: "No-code databases and apps" },
    ],
  },
  {
    name: "Customer Data, Feedback & Research",
    tools: [
      { name: "Segment / mParticle", sub: "CDP", what: "Customer data platform / unification" },
      { name: "SurveyMonkey (Momentive)", sub: "Survey", what: "Surveys and feedback collection" },
      { name: "Qualtrics", sub: "Experience mgmt", what: "Experience management (CX, EX)" },
      { name: "Pendo / Sprig", sub: "Product feedback", what: "In-product analytics + feedback" },
      { name: "Dovetail / Maze", sub: "User research", what: "Research repository + testing" },
      { name: "Trustpilot / G2", sub: "Reviews", what: "Customer + software reviews" },
    ],
  },
  {
    name: "Logistics, Supply Chain & Inventory",
    tools: [
      { name: "Cin7 / Unleashed", sub: "Inventory/OMS", what: "Inventory and order management" },
      { name: "Starshipit / Shippit", sub: "Shipping", what: "Shipping + fulfilment automation" },
      { name: "project44 / FourKites", sub: "Freight visibility", what: "Real-time supply-chain visibility" },
      { name: "Manhattan / Fishbowl", sub: "WMS", what: "Warehouse management" },
      { name: "Onfleet / Locate2u", sub: "Last-mile", what: "Delivery routing and dispatch" },
      { name: "Felix / Ariba", sub: "Procure/supply", what: "Supplier and procurement management" },
    ],
  },
  {
    name: "Workplace Ops, Safety & AgTech",
    tools: [
      { name: "SafetyCulture (iAuditor)", sub: "Frontline ops", what: "Inspections, safety, frontline ops" },
      { name: "Damstra", sub: "Workforce safety", what: "Workforce safety, compliance, site access" },
      { name: "Whispir", sub: "Comms automation", what: "Communications + workflow automation" },
      { name: "Halter", sub: "AgTech", what: "Virtual fencing + cattle/farm management" },
      { name: "Figured / Farmlands", sub: "AgTech", what: "Farm financial + management" },
      { name: "Timely / Fresha", sub: "Field/bookings", what: "Appointment booking for services" },
    ],
  },
];

export const MARKET_MAP_TOTAL_TOOLS = MARKET_MAP.reduce((s, c) => s + c.tools.length, 0); // 316
export const MARKET_MAP_TOTAL_CATEGORIES = MARKET_MAP.length; // 41

/** Indicative headline band from the market map: typical consolidation 70-80%. */
export const CONSOLIDATION_BAND = { low: 0.7, high: 0.8 };
